import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { wishlistApi } from '../lib/api';
import { useCart } from '../contexts/CartContext';
import { useLang } from '../contexts/LanguageContext';
import { Heart, ShoppingBag, Trash2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

export default function AccountWishlistPage() {
  const { wishlist, loading, refreshData } = useOutletContext();
  const { addToCart } = useCart();
  const { t, tField } = useLang();
  const { toast } = useToast();

  const handleRemove = async (productId) => {
    try {
      await wishlistApi.remove(productId);
      toast({ title: 'Rimosso dalla wishlist' });
      refreshData();
    } catch (error) {
      toast({ title: 'Errore', variant: 'destructive' });
    }
  };

  const handleAddToCart = async (product) => {
    if (!product.sizes?.length || !product.colors?.length) {
      toast({
        title: 'Vai alla pagina prodotto',
        description: 'Seleziona taglia e colore',
      });
      return;
    }

    const result = await addToCart({
      product_id: product.product_id,
      quantity: 1,
      size: product.sizes[0],
      color: product.colors[0],
    });

    if (result.success) {
      toast({ title: 'Aggiunto al carrello' });
    } else {
      toast({ title: 'Errore', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="aspect-[3/4] bg-[#E5E0D8]" />
            <div className="mt-3 h-4 bg-[#E5E0D8] w-3/4" />
            <div className="mt-2 h-4 bg-[#E5E0D8] w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (wishlist.length === 0) {
    return (
      <div className="text-center py-16">
        <Heart className="w-16 h-16 mx-auto text-[#E5E0D8] mb-4" />
        <h2 className="text-xl font-medium mb-2">{t({ it: 'Wishlist vuota', en: 'Empty wishlist' })}</h2>
        <p className="text-[#57534E] mb-6">
          {t({ it: 'Aggiungi i tuoi prodotti preferiti alla wishlist', en: 'Add your favourite products to your wishlist' })}
        </p>
        <Link
          to="/shop"
          data-testid="browse-products-btn"
          className="btn-primary inline-block"
        >
          {t({ it: 'Esplora i Prodotti', en: 'Explore Products' })}
        </Link>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-light mb-6">{t({ it: 'La Mia Wishlist', en: 'My Wishlist' })}</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {wishlist.map((product, idx) => (
          <div key={product.product_id} data-testid={`wishlist-item-${idx}`} className="group">
            <Link to={`/product/${product.product_id}`} className="block">
              <div className="aspect-[3/4] bg-[#F2EFE9] overflow-hidden">
                <img
                  src={product.images?.[0] || 'https://via.placeholder.com/300x400'}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </Link>
            <div className="mt-3">
              <Link
                to={`/product/${product.product_id}`}
                className="font-medium hover:text-[#4A5D23] transition-colors"
              >
                {tField(product, 'name')}
              </Link>
              <p className="text-[#4A5D23] font-medium mt-1">€{product.price?.toFixed(2)}</p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => handleAddToCart(product)}
                  data-testid={`wishlist-add-cart-${idx}`}
                  className="flex-1 py-2 bg-[#1C1917] text-white text-sm flex items-center justify-center gap-2 hover:bg-[#57534E] transition-colors"
                >
                  <ShoppingBag className="w-4 h-4" />
                  {t({ it: 'Aggiungi', en: 'Add' })}
                </button>
                <button
                  onClick={() => handleRemove(product.product_id)}
                  data-testid={`wishlist-remove-${idx}`}
                  className="p-2 border border-[#E5E0D8] hover:border-[#C05746] hover:text-[#C05746] transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
